import Agilite from 'agilite'
import store from '../../../../store'
import BillingEnums from '../../utils/enums'
import { logAuditEvent, AuditActions, AuditEntityTypes } from './auditTrail'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

/**
 * Get patient ledger transactions
 * @param {Object} filter - MongoDB filter object
 * @param {number} page - Page number
 * @param {number} pageLimit - Items per page
 * @param {string} projection - Field projection
 * @param {Object} sort - Sort criteria
 * @returns {Promise} Ledger transactions
 */
export const getPatientLedgerTransactions = (filter = {}, page = 1, pageLimit = 50, projection = '', sort = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        filter = {
          ...filter,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.PATIENT_LEDGER,
          BillingEnums.routeKeys.READ,
          {
            filter: JSON.stringify(filter),
            page,
            pageLimit,
            projection,
            sort: JSON.stringify(sort || { date: -1 })
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get patient ledger summary with separate medical aid and patient balances
 * @param {string} billingId - Billing ID (primary identifier for billing data)
 * @param {string} patientId - Patient ID (optional, for specific patient within billing account)
 * @returns {Promise} Ledger summary with separated balances
 */
export const getPatientLedgerSummary = (billingId, patientId) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const pipeline = [
          {
            $match: {
              entityRef: store.getState().core.entity._id,
              billingId: billingId,
              ...(patientId ? { patientId } : {})
            }
          },
          // Join with invoices to check private status
          {
            $lookup: {
              from: 'invoices',
              localField: 'invoiceNo',
              foreignField: 'invoiceNo',
              as: 'invoiceDetails'
            }
          },
          {
            $addFields: {
              // Check if invoice is private (accountGroup = 'private' OR goodXStatus = 'Private' OR no goodXTicketNo)
              isPrivateInvoice: {
                $cond: [
                  { $eq: ['$type', BillingEnums.transactionTypes.INVOICE] },
                  {
                    $or: [
                      { $eq: [{ $arrayElemAt: ['$invoiceDetails.accountGroup', 0] }, 'private'] },
                      { $eq: [{ $arrayElemAt: ['$invoiceDetails.goodXStatus', 0] }, 'Private'] },
                      { $eq: [{ $arrayElemAt: ['$invoiceDetails.goodXTicketNo', 0] }, null] },
                      { $eq: [{ $arrayElemAt: ['$invoiceDetails.goodXTicketNo', 0] }, ''] }
                    ]
                  },
                  false
                ]
              }
            }
          },
          {
            $group: {
              _id: null,
              totalDebits: { $sum: '$debit' },
              totalCredits: { $sum: '$credit' },
              // Separate medical aid and patient portions
              medicalAidDebits: {
                $sum: {
                  $cond: [
                    {
                      $and: [
                        { $eq: ['$type', BillingEnums.transactionTypes.INVOICE] },
                        { $ne: ['$isPrivateInvoice', true] }
                      ]
                    },
                    { $ifNull: ['$medicalAidAmount', 0] },
                    0
                  ]
                }
              },
              medicalAidCredits: {
                $sum: {
                  $cond: [
                    { $eq: ['$type', BillingEnums.transactionTypes.MEDICAL_AID_PAYMENT] },
                    { $ifNull: ['$credit', 0] },
                    0
                  ]
                }
              },
              patientDebits: {
                $sum: {
                  $cond: [
                    { $eq: ['$type', BillingEnums.transactionTypes.INVOICE] },
                    { $ifNull: ['$patientAmount', 0] },
                    0
                  ]
                }
              },
              patientCredits: {
                $sum: {
                  $cond: [
                    {
                      $and: [
                        { $ne: ['$type', BillingEnums.transactionTypes.MEDICAL_AID_PAYMENT] },
                        {
                          $in: [
                            '$type',
                            [
                              BillingEnums.transactionTypes.PAYMENT,
                              BillingEnums.transactionTypes.CREDIT,
                              BillingEnums.transactionTypes.WRITE_OFF
                            ]
                          ]
                        }
                      ]
                    },
                    { $ifNull: ['$credit', 0] },
                    0
                  ]
                }
              },
              transactionCount: { $sum: 1 },
              lastTransactionDate: { $max: '$date' },
              firstTransactionDate: { $min: '$date' }
            }
          },
          {
            $project: {
              _id: 0,
              totalDebits: 1,
              totalCredits: 1,
              currentBalance: { $subtract: ['$totalDebits', '$totalCredits'] },
              medicalAidBalance: { $subtract: ['$medicalAidDebits', '$medicalAidCredits'] },
              patientBalance: { $subtract: ['$patientDebits', '$patientCredits'] },
              transactionCount: 1,
              lastTransactionDate: 1,
              firstTransactionDate: 1
            }
          }
        ]

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.PATIENT_LEDGER,
          BillingEnums.routeKeys.AGGREGATE,
          {
            pipeline: JSON.stringify(pipeline)
          }
        )

        // Get aging breakdown
        const agingPipeline = [
          {
            $match: {
              entityRef: store.getState().core.entity._id,
              billingId: billingId,
              ...(patientId ? { patientId } : {}),
              type: BillingEnums.transactionTypes.INVOICE,
              status: { $ne: 'Paid' }
            }
          },
          {
            $addFields: {
              daysSinceInvoice: {
                $divide: [{ $subtract: ['$$NOW', '$date'] }, 1000 * 60 * 60 * 24]
              }
            }
          },
          {
            $addFields: {
              agingBucket: {
                $switch: {
                  branches: [
                    { case: { $lte: ['$daysSinceInvoice', 29] }, then: '0-29' },
                    { case: { $lte: ['$daysSinceInvoice', 59] }, then: '30-59' },
                    { case: { $lte: ['$daysSinceInvoice', 89] }, then: '60-89' }
                  ],
                  default: '90+'
                }
              }
            }
          },
          {
            $group: {
              _id: '$agingBucket',
              amount: { $sum: '$debit' },
              count: { $sum: 1 }
            }
          }
        ]

        const agingResponse = await agilite.Connectors.execute(
          BillingEnums.profileKeys.PATIENT_LEDGER,
          BillingEnums.routeKeys.AGGREGATE,
          {
            pipeline: JSON.stringify(agingPipeline)
          }
        )

        const summary = response.data[0] || {
          totalDebits: 0,
          totalCredits: 0,
          currentBalance: 0,
          transactionCount: 0
        }

        // Format aging buckets
        const agingBuckets = {
          '0-29': 0,
          '30-59': 0,
          '60-89': 0,
          '90+': 0
        }

        agingResponse.data.forEach((bucket) => {
          agingBuckets[bucket._id] = bucket.amount
        })

        resolve({
          ...summary,
          agingBuckets
        })
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Create ledger transaction
 * @param {Object} data - Transaction data
 * @returns {Promise} Created transaction
 */
export const createLedgerTransaction = (data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Validate transaction type
        const validTypes = Object.values(BillingEnums.transactionTypes)
        if (!validTypes.includes(data.type)) {
          throw new Error(`Invalid transaction type: ${data.type}`)
        }

        // Calculate transaction amounts based on type
        let transactionAmount = 0
        let medicalAidPortion = data.medicalAidAmount || 0
        let patientPortion = data.patientAmount || 0

        switch (data.type) {
          case BillingEnums.transactionTypes.INVOICE:
            transactionAmount = data.debit || 0
            break
          case BillingEnums.transactionTypes.PAYMENT:
          case BillingEnums.transactionTypes.MEDICAL_AID_PAYMENT:
          case BillingEnums.transactionTypes.CREDIT:
          case BillingEnums.transactionTypes.CREDIT_NOTE:
          case BillingEnums.transactionTypes.WRITE_OFF:
            transactionAmount = -(data.credit || 0)
            break
          case BillingEnums.transactionTypes.DEBIT:
          case BillingEnums.transactionTypes.ADJUSTMENT:
            if (data.debit > 0) {
              transactionAmount = data.debit
            } else if (data.credit > 0) {
              transactionAmount = -data.credit
            }
            break
          case BillingEnums.transactionTypes.REFUND:
            transactionAmount = data.debit || 0 // Refunds increase what patient owes
            break
          default:
            transactionAmount = (data.debit || 0) - (data.credit || 0)
        }

        // Ensure balance tracking fields
        data = {
          ...data,
          transactionAmount,
          medicalAidAmount: medicalAidPortion,
          patientAmount: patientPortion,
          entityRef: store.getState().core.entity._id,
          createdBy: data.createdBy || store.getState().auth.agiliteUser?._id,
          createdAt: data.createdAt || new Date(),
          date: data.date ? new Date(data.date) : new Date()
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.PATIENT_LEDGER,
          BillingEnums.routeKeys.CREATE,
          {
            data: JSON.stringify(data)
          }
        )

        // Log audit trail for ledger transaction
        await logAuditEvent(
          AuditActions.CREATE,
          AuditEntityTypes.PATIENT_LEDGER,
          response.data._id,
          `${data.type} transaction: ${data.description}`,
          {
            type: data.type,
            debit: data.debit || 0,
            credit: data.credit || 0,
            transactionAmount: data.transactionAmount,
            patientId: data.patientId,
            billingId: data.billingId, // Link to follow-up account
            invoiceNo: data.invoiceNo,
            reference: data.reference,
            paymentMethod: data.paymentMethod
          }
        )

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Record payment transaction
 * @param {Object} paymentData - Payment details
 * @returns {Promise} Created payment transaction
 */
export const recordPayment = (paymentData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Validate EFT transactions have a reference
        if (
          paymentData.paymentMethod === BillingEnums.paymentMethods.EFT &&
          (!paymentData.reference || paymentData.reference.trim().length === 0)
        ) {
          throw new Error('EFT payments must include a reference number')
        }

        // Validate payment amount against outstanding balances to prevent overpayment
        const paymentAmount = parseFloat(paymentData.amount) || 0
        const billingId = paymentData.billingId

        if (paymentAmount > 0 && billingId) {
          const balanceSummary = await getPatientLedgerSummary(billingId, paymentData.patientId)

          if (balanceSummary) {
            const patientBalance = balanceSummary.patientBalance || 0
            const medicalAidBalance = balanceSummary.medicalAidBalance || 0

            // Check for overpayment scenarios
            if (paymentData.isMedicalAid) {
              // Medical aid payment validation
              if (paymentAmount > medicalAidBalance) {
                if (patientBalance > 0) {
                  throw new Error(
                    `Medical aid payment of R${paymentAmount.toFixed(
                      2
                    )} exceeds medical aid outstanding balance of R${medicalAidBalance.toFixed(2)}. ` +
                      `Patient balance of R${patientBalance.toFixed(2)} is still outstanding. ` +
                      'Consider allocating part of this payment to patient portion.'
                  )
                } else {
                  // Allow overpayment if no patient balance (creates credit)
                  console.warn(
                    `Medical aid overpayment of R${(paymentAmount - medicalAidBalance).toFixed(
                      2
                    )} will create account credit`
                  )
                }
              }
            } else {
              // Patient payment validation
              if (paymentAmount > patientBalance) {
                if (medicalAidBalance > 0) {
                  throw new Error(
                    `Patient payment of R${paymentAmount.toFixed(
                      2
                    )} exceeds patient outstanding balance of R${patientBalance.toFixed(2)}. ` +
                      `Medical aid balance of R${medicalAidBalance.toFixed(2)} is still outstanding. ` +
                      `Maximum patient payment allowed: R${patientBalance.toFixed(2)}`
                  )
                } else {
                  // Allow overpayment if no medical aid balance (creates credit)
                  console.warn(
                    `Patient overpayment of R${(paymentAmount - patientBalance).toFixed(2)} will create account credit`
                  )
                }
              }
            }
          }
        }

        const data = {
          patientId: paymentData.patientId,
          billingId: paymentData.billingId,
          accountNo: paymentData.accountNo,
          date: paymentData.paymentDate || new Date(),
          type: paymentData.isMedicalAid
            ? BillingEnums.transactionTypes.MEDICAL_AID_PAYMENT
            : BillingEnums.transactionTypes.PAYMENT,
          description: `Payment - ${paymentData.paymentMethod}`,
          invoiceNo: paymentData.invoiceNo,
          debit: 0,
          credit: parseFloat(paymentData.amount) || 0,
          paymentMethod: paymentData.paymentMethod,
          reference: paymentData.reference,
          status: 'Processed',
          notes: paymentData.notes
        }

        const response = await createLedgerTransaction(data)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Record refund transaction
 * @param {Object} refundData - Refund details
 * @returns {Promise} Created refund transaction
 */
export const recordRefund = (refundData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const transaction = {
          type: BillingEnums.transactionTypes.REFUND,
          description: `Refund - ${refundData.reason}`,
          debit: parseFloat(refundData.amount), // Refunds increase what's owed
          credit: 0,
          transactionAmount: parseFloat(refundData.amount),
          paymentMethod: refundData.paymentMethod,
          reference: refundData.reference,
          patientId: refundData.patientId,
          billingId: refundData.billingId,
          invoiceNo: refundData.invoiceNo,
          notes: refundData.notes,
          status: 'Refunded',
          refundReason: refundData.reason,
          originalPaymentRef: refundData.originalPaymentRef
        }

        const response = await createLedgerTransaction(transaction)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Record medical aid remittance payment
 * @param {Object} remittanceData - Remittance payment details
 * @returns {Promise} Created remittance transaction
 */
export const recordRemittancePayment = (remittanceData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const transaction = {
          type: BillingEnums.transactionTypes.MEDICAL_AID_PAYMENT,
          description: `Medical Aid Remittance - ${remittanceData.medicalAid} - Receipt: ${remittanceData.receiptNumber}`,
          debit: 0,
          credit: parseFloat(remittanceData.amount),
          transactionAmount: -parseFloat(remittanceData.amount),
          paymentMethod: 'medical_aid_remittance',
          reference: remittanceData.receiptNumber,
          patientId: remittanceData.patientId,
          billingId: remittanceData.billingId,
          invoiceNo: remittanceData.invoiceNo,
          notes: `Remittance Date: ${remittanceData.remittanceDate}`,
          status: 'Processed',
          medicalAidAmount: parseFloat(remittanceData.amount),
          patientAmount: 0,
          remittanceRef: remittanceData.remittanceId
        }

        const response = await createLedgerTransaction(transaction)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Record credit adjustment
 * @param {Object} creditData - Credit adjustment details
 * @returns {Promise} Created credit transaction
 */
export const recordCredit = (creditData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const transaction = {
          type: BillingEnums.transactionTypes.CREDIT,
          description: creditData.description || 'Credit Adjustment',
          debit: 0,
          credit: parseFloat(creditData.amount),
          transactionAmount: -parseFloat(creditData.amount),
          patientId: creditData.patientId,
          billingId: creditData.billingId,
          invoiceNo: creditData.invoiceNo,
          notes: creditData.notes,
          status: 'Processed',
          creditReason: creditData.reason,
          authorization: creditData.authorization
        }

        const response = await createLedgerTransaction(transaction)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Record debit adjustment
 * @param {Object} debitData - Debit adjustment details
 * @returns {Promise} Created debit transaction
 */
export const recordDebit = (debitData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const transaction = {
          type: BillingEnums.transactionTypes.DEBIT,
          description: debitData.description || 'Debit Adjustment',
          debit: parseFloat(debitData.amount),
          credit: 0,
          transactionAmount: parseFloat(debitData.amount),
          patientId: debitData.patientId,
          billingId: debitData.billingId,
          invoiceNo: debitData.invoiceNo,
          notes: debitData.notes,
          status: 'Processed',
          debitReason: debitData.reason,
          authorization: debitData.authorization
        }

        const response = await createLedgerTransaction(transaction)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Record adjustment transaction
 * @param {Object} adjustmentData - Adjustment details
 * @returns {Promise} Created adjustment transaction
 */
export const recordAdjustment = (adjustmentData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const isDebit = adjustmentData.adjustmentType === 'debit'

        const transaction = {
          type: BillingEnums.transactionTypes.ADJUSTMENT,
          description: adjustmentData.reason,
          debit: isDebit ? parseFloat(adjustmentData.amount) : 0,
          credit: isDebit ? 0 : parseFloat(adjustmentData.amount),
          transactionAmount: isDebit ? parseFloat(adjustmentData.amount) : -parseFloat(adjustmentData.amount),
          patientId: adjustmentData.patientId,
          billingId: adjustmentData.billingId,
          accountNo: adjustmentData.accountNo,
          notes: adjustmentData.notes,
          status: 'Processed'
        }

        const response = await createLedgerTransaction(transaction)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Record write-off transaction
 * @param {Object} writeOffData - Write-off details
 * @returns {Promise} Created write-off transaction
 */
export const recordWriteOff = (writeOffData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const transaction = {
          type: BillingEnums.transactionTypes.WRITE_OFF,
          description: `Write Off - ${writeOffData.reason.replace(/_/g, ' ')}`,
          debit: 0,
          credit: parseFloat(writeOffData.amount),
          transactionAmount: -parseFloat(writeOffData.amount),
          authorization: writeOffData.authorization,
          patientId: writeOffData.patientId,
          billingId: writeOffData.billingId,
          notes: writeOffData.notes,
          status: 'Written Off'
        }

        const response = await createLedgerTransaction(transaction)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get transaction history for account
 * @param {Object} dateRange - Date range filter
 * @returns {Promise} Transaction history
 */
export const getTransactionHistory = (billingId, dateRange = {}) => {
  return new Promise((resolve, reject) => {
    let query = {
      filter: {
        billingId,
        // Optional date range filter
        ...(dateRange.from && { date: { $gte: dateRange.from } }),
        ...(dateRange.to && { date: { $lte: dateRange.to } })
      },
      sort: { date: -1 }, // Sort by most recent
      limit: 1000 // Limit to the last 1000 transactions for performance
    }
    ;(async () => {
      try {
        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.PATIENT_LEDGER,
          BillingEnums.routeKeys.READ,
          query
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get statement data for patient
 * @param {string} patientId - Patient ID
 * @param {Object} options - Statement options
 * @returns {Promise} Statement data
 */
export const getPatientStatement = (patientId, options = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const { startDate, endDate, includeZeroBalance } = options

        const filter = {
          patientId,
          entityRef: store.getState().core.entity._id
        }

        if (startDate && endDate) {
          filter.date = {
            $gte: startDate,
            $lte: endDate
          }
        }

        // Get transactions
        const transactions = await getPatientLedgerTransactions(filter, 1, 1000, '', { date: 1 })

        // Get summary
        const summary = await getPatientLedgerSummary(patientId, null)

        // Calculate balance brought forward if date range specified
        let balanceBroughtForward = 0
        if (startDate) {
          const previousTransactions = await getPatientLedgerTransactions(
            {
              patientId,
              entityRef: store.getState().core.entity._id,
              date: { $lt: startDate }
            },
            1,
            1000
          )

          balanceBroughtForward = previousTransactions.reduce((sum, t) => sum + t.transactionAmount, 0)
        }

        // Filter out zero balance items if requested
        let finalTransactions = transactions
        if (!includeZeroBalance) {
          finalTransactions = transactions.filter((t) => t.transactionAmount !== 0)
        }

        resolve({
          transactions: finalTransactions,
          summary,
          balanceBroughtForward,
          statementDate: new Date().toISOString(),
          dateRange: { startDate, endDate }
        })
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Delete all patient ledger transactions (for testing purposes)
 * @returns {Promise}
 */
export const deleteAllPatientLedgerTransactions = () => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Delete ALL patient ledger transactions without any filter
        const filter = {}

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.PATIENT_LEDGER,
          BillingEnums.routeKeys.DELETE_MANY,
          {
            filter: JSON.stringify(filter)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}
